// ── DSLRequestForm ✦ — calm parent → Safeguarding Lead channel ──
//
// Quiet, never alarmist. Submits to the existing safeguarding
// pipeline via the dashboard's token-gated endpoint. Confirmation
// copy is locked by /app/memory/PARENT_DASHBOARD_PRD.md §5.5 +
// Craig answer 4 (no SLAs / guarantees / countdowns).
import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { SealCheck, Lifebuoy, ArrowRight } from '@phosphor-icons/react';
import {
  API, PANEL_BORDER, TEXT, TEXT_MUTED, BLUE, VIOLET, ROSE, SAGE, FONT_STACK,
} from './palette';

export const DSLRequestForm = ({ token, label }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!email.trim() || !message.trim() || submitting) return;
    setSubmitting(true); setError('');
    try {
      const r = await axios.post(
        `${API}/parent/dashboard/${token}/contact-dsl`,
        { parent_name: name.trim(), parent_email: email.trim(), message: message.trim() },
      );
      setConfirmation(r.data?.confirmation || 'Thanks for getting in touch ✦.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong sending that — please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [name, email, message, token, submitting]);

  if (confirmation) {
    return (
      <div
        data-testid="parent-dashboard-dsl-confirmation"
        style={{
          padding: 16, borderRadius: 14,
          background: `${SAGE}10`, border: `1px solid ${SAGE}45`,
          color: TEXT, fontSize: 13.5, lineHeight: 1.6,
          display: 'flex', gap: 10, alignItems: 'flex-start',
        }}
      >
        <SealCheck size={16} weight="fill" color={SAGE} style={{ marginTop: 2, flexShrink: 0 }} />
        <span>{confirmation}</span>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        data-testid="parent-dashboard-dsl-open"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '11px 18px', borderRadius: 999,
          background: 'rgba(255,255,255,0.05)',
          border: `1px solid ${BLUE}55`,
          color: TEXT, fontWeight: 700, fontSize: 13, letterSpacing: '0.01em',
          cursor: 'pointer',
        }}
      >
        <Lifebuoy size={14} weight="fill" color={BLUE} />
        {label}
        <ArrowRight size={12} weight="bold" color={TEXT_MUTED} />
      </button>
    );
  }

  const inputStyle = {
    padding: '12px 14px', borderRadius: 12,
    background: 'rgba(255,255,255,0.04)',
    border: `1px solid ${PANEL_BORDER}`,
    color: TEXT, fontSize: 13.5, fontFamily: FONT_STACK,
    outline: 'none',
  };

  return (
    <form
      onSubmit={submit}
      data-testid="parent-dashboard-dsl-form"
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <p style={{ margin: 0, color: TEXT_MUTED, fontSize: 12.5, lineHeight: 1.55 }}>
        Share a few details and someone from the EventDrop safeguarding team will be in touch.
      </p>
      <input
        type="text"
        placeholder="Your name (optional)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid="parent-dashboard-dsl-name"
        style={inputStyle}
      />
      <input
        type="email"
        placeholder="Your email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid="parent-dashboard-dsl-email"
        style={inputStyle}
      />
      <textarea
        placeholder="What would you like to chat about?"
        required
        rows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        data-testid="parent-dashboard-dsl-message"
        style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }}
      />
      {error && (
        <p data-testid="parent-dashboard-dsl-error" style={{ margin: 0, color: ROSE, fontSize: 12.5 }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button
          type="submit"
          disabled={submitting || !email.trim() || !message.trim()}
          data-testid="parent-dashboard-dsl-submit"
          style={{
            padding: '11px 20px', borderRadius: 999,
            background: submitting
              ? 'rgba(255,255,255,0.06)'
              : `linear-gradient(135deg, ${BLUE}, ${VIOLET})`,
            border: 'none',
            color: TEXT, fontWeight: 800, fontSize: 13,
            cursor: submitting ? 'default' : 'pointer',
            opacity: submitting ? 0.75 : 1,
          }}
        >
          {submitting ? 'Sending…' : 'Send message ✦'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="parent-dashboard-dsl-cancel"
          style={{
            padding: '11px 16px', borderRadius: 999,
            background: 'transparent', border: `1px solid ${PANEL_BORDER}`,
            color: TEXT_MUTED, fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};
